import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, CalendarDays, CheckCircle, Clock, Users, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AttendanceOverviewPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | taken | pending

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/attendance-overview?limit=60`, {
        withCredentials: true,
      });
      setSessions(res.data || []);
    } catch (error) {
      console.error("Error fetching attendance overview:", error);
      toast.error("Failed to load attendance overview");
    } finally {
      setLoading(false);
    }
  };

  const filtered = sessions.filter((s) => {
    if (filter === "taken") return s.attendance_taken;
    if (filter === "pending") return !s.attendance_taken;
    return true;
  });

  const stats = {
    total: sessions.length,
    taken: sessions.filter((s) => s.attendance_taken).length,
    pending: sessions.filter((s) => !s.attendance_taken).length,
    guests: sessions.reduce((acc, s) => acc + (s.guest_count || 0), 0),
  };

  if (loading) {
    return (
      <Card className="p-12 bg-slate-900/50 flex justify-center" data-testid="attendance-panel-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="attendance-overview-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 bg-slate-900/50">
          <p className="text-xs text-slate-400">Recent Sessions</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </Card>
        <Card className="p-4 bg-slate-900/50">
          <p className="text-xs text-slate-400 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Attendance Taken</p>
          <p className="text-2xl font-bold text-emerald-400">{stats.taken}</p>
        </Card>
        <Card className="p-4 bg-slate-900/50">
          <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3 text-amber-400" /> Pending</p>
          <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
        </Card>
        <Card className="p-4 bg-slate-900/50">
          <p className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3 h-3 text-purple-400" /> Guest Players</p>
          <p className="text-2xl font-bold text-purple-400">{stats.guests}</p>
        </Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {[
            { k: "all", label: "All" },
            { k: "pending", label: "Pending" },
            { k: "taken", label: "Taken" },
          ].map((f) => (
            <Button
              key={f.k}
              size="sm"
              variant={filter === f.k ? "default" : "outline"}
              onClick={() => setFilter(f.k)}
              className={filter === f.k ? "bg-blue-600 hover:bg-blue-700" : "border-slate-600"}
              data-testid={`attendance-filter-${f.k}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          size="sm"
          className="border-white/20 text-white hover:bg-slate-900/50"
          data-testid="attendance-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card className="p-6 bg-slate-900/50">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <CalendarDays className="w-16 h-16 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400">No sessions match this filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Signups</TableHead>
                  <TableHead>Guests</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Present / Absent / MC</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} className="hover:bg-slate-800/30">
                    <TableCell className="text-sm">{formatDate(s.session_date)}</TableCell>
                    <TableCell className="font-medium text-white">{s.team_name}</TableCell>
                    <TableCell className="text-slate-300 capitalize">{s.session_type || "-"}</TableCell>
                    <TableCell className="text-slate-300">{s.signup_count}</TableCell>
                    <TableCell>
                      {s.guest_count > 0 ? (
                        <Badge className="bg-purple-500/20 text-purple-300">{s.guest_count}</Badge>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.attendance_taken ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400">Taken</Badge>
                      ) : (
                        <Badge className="bg-amber-500/20 text-amber-400">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {s.attendance_taken ? (
                        <span>
                          <span className="text-emerald-400">{s.present_count}</span>
                          {" / "}
                          <span className="text-red-400">{s.absent_count}</span>
                          {" / "}
                          <span className="text-yellow-400">{s.mc_count}</span>
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/academy/attendance/${s.id}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                          data-testid={`view-attendance-${s.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />
                          View
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
